import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLocale } from '../hooks/useLocale';

type TranslationKey = Parameters<ReturnType<typeof useLocale>['t']>[0];

const languages = [
  { code: 'en', label: 'English' },
  { code: 'ru', label: 'Русский' },
  { code: 'de', label: 'Deutsch' },
  { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' },
] as const;

const stats: Array<{ value: TranslationKey; label: TranslationKey }> = [
  { value: 'landing_stat_formats_value', label: 'landing_stat_formats_label' },
  { value: 'landing_stat_flow_value', label: 'landing_stat_flow_label' },
  { value: 'landing_stat_languages_value', label: 'landing_stat_languages_label' },
];

const features: Array<{ icon: string; title: TranslationKey; body: TranslationKey }> = [
  { icon: 'EPUB', title: 'landing_feature_import_title', body: 'landing_feature_import_body' },
  { icon: 'TAP', title: 'landing_feature_translate_title', body: 'landing_feature_translate_body' },
  { icon: 'SRS', title: 'landing_feature_cards_title', body: 'landing_feature_cards_body' },
  { icon: 'TXT', title: 'landing_feature_settings_title', body: 'landing_feature_settings_body' },
];

const steps: Array<{ number: string; title: TranslationKey; body: TranslationKey }> = [
  { number: '01', title: 'landing_step_import_title', body: 'landing_step_import_body' },
  { number: '02', title: 'landing_step_read_title', body: 'landing_step_read_body' },
  { number: '03', title: 'landing_step_review_title', body: 'landing_step_review_body' },
];

const faq: Array<{ question: TranslationKey; answer: TranslationKey }> = [
  { question: 'landing_faq_formats_q', answer: 'landing_faq_formats_a' },
  { question: 'landing_faq_login_q', answer: 'landing_faq_login_a' },
  { question: 'landing_faq_language_q', answer: 'landing_faq_language_a' },
];

function LoginButton({ className = '' }: { className?: string }) {
  const { token } = useAuth();
  const { t } = useLocale();

  return (
    <Link
      to={token ? '/dashboard' : '/login'}
      className={`inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-[#111827] px-6 py-3 text-sm font-semibold text-white shadow-[0_18px_45px_rgba(17,24,39,0.22)] transition hover:-translate-y-0.5 hover:bg-[#0b1220] focus:outline-none focus:ring-4 focus:ring-[#80CBC4]/40 ${className}`}
      aria-label={token ? t('dashboard') : t('landing_login_aria')}
    >
      <span>{token ? t('dashboard') : t('get_started')}</span>
      <span aria-hidden="true">{'->'}</span>
    </Link>
  );
}

function ReaderMockup() {
  const { t } = useLocale();

  return (
    <div className="relative mx-auto w-full max-w-[360px]">
      <div className="absolute -left-5 top-14 hidden w-24 rounded-2xl bg-white/90 p-3 text-xs font-semibold text-slate-700 shadow-xl shadow-slate-900/10 ring-1 ring-slate-900/5 backdrop-blur sm:block">
        <div className="mb-1 text-[10px] uppercase tracking-[0.18em] text-teal-700">
          {t('landing_mock_dictionary')}
        </div>
        <div className="text-2xl font-black text-slate-950">128</div>
        <div className="text-slate-500">{t('landing_mock_saved_words')}</div>
      </div>

      <div className="absolute -right-3 bottom-20 z-20 hidden w-32 rounded-2xl bg-[#0f766e] p-3 text-white shadow-2xl shadow-teal-900/25 sm:block">
        <div className="text-[10px] uppercase tracking-[0.18em] text-teal-100">
          {t('landing_mock_review')}
        </div>
        <div className="mt-2 flex items-end gap-1">
          <span className="text-3xl font-black">12</span>
          <span className="pb-1 text-xs text-teal-100">{t('landing_mock_today')}</span>
        </div>
      </div>

      <div className="rounded-[2.4rem] bg-slate-950 p-3 shadow-[0_35px_90px_rgba(15,23,42,0.35)]">
        <div className="overflow-hidden rounded-[1.85rem] bg-[#f8f5ef]">
          <div className="flex items-center justify-between border-b border-slate-200/70 px-5 py-4 text-slate-500">
            <span className="text-xs font-bold uppercase tracking-[0.24em]">{t('reader')}</span>
            <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-700 shadow-sm">
              64%
            </span>
          </div>
          <div className="space-y-5 px-6 py-7">
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-[0.22em] text-teal-700">
                {t('landing_mock_chapter')}
              </p>
              <h3 className="text-xl font-black leading-tight text-slate-950">
                {t('landing_mock_title')}
              </h3>
            </div>

            <div className="space-y-3 text-[15px] leading-8 text-slate-700">
              <p>{t('landing_mock_line_1')}</p>
              <p>
                {t('landing_mock_line_2_prefix')}{' '}
                <span className="relative inline-flex items-center rounded-lg bg-[#e1f4ef] px-1.5 font-bold text-teal-900 ring-1 ring-teal-200">
                  {t('landing_mock_word')}
                </span>{' '}
                {t('landing_mock_line_2_suffix')}
              </p>
              <p>{t('landing_mock_line_3')}</p>
            </div>

            <div className="rounded-2xl bg-white p-4 shadow-lg shadow-slate-900/8 ring-1 ring-slate-900/5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                    {t('translationApi')}
                  </div>
                  <div className="mt-1 text-lg font-black text-slate-950">
                    {t('landing_mock_translation')}
                  </div>
                </div>
                <button
                  type="button"
                  className="rounded-full bg-[#1565C0] px-3 py-2 text-xs font-bold text-white"
                  aria-label={t('save')}
                >
                  {t('save')}
                </button>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-3 border-t border-slate-200/70 bg-white/75 text-center text-[11px] font-bold text-slate-500">
            <div className="px-2 py-3 text-[#1565C0]">{t('library')}</div>
            <div className="px-2 py-3">{t('dictionary')}</div>
            <div className="px-2 py-3">{t('settings')}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function LandingPage() {
  const { t, locale, setLocale } = useLocale();

  return (
    <div className="min-h-screen bg-[#f7faf9] text-slate-950">
      <header className="sticky top-0 z-50 border-b border-slate-900/5 bg-[#f7faf9]/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3 sm:px-6 lg:px-8">
          <Link to="/" className="flex min-w-0 items-center gap-3" aria-label={t('app_name')}>
            <img src="./icon-192.png" alt="" className="h-9 w-9 rounded-xl shadow-sm" />
            <span className="truncate text-base font-black tracking-tight sm:text-lg">
              {t('app_name')}
            </span>
          </Link>

          <nav className="flex items-center gap-2 sm:gap-3">
            <Link
              to="/privacy"
              className="hidden text-sm font-semibold text-slate-600 transition hover:text-slate-950 sm:inline-flex"
            >
              {t('privacy')}
            </Link>
            <label className="sr-only" htmlFor="locale">
              {t('interface_language')}
            </label>
            <select
              id="locale"
              value={locale}
              onChange={(e) => setLocale(e.target.value as typeof locale)}
              className="h-10 rounded-full border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 shadow-sm outline-none transition hover:border-slate-300 focus:border-[#1565C0] focus:ring-4 focus:ring-blue-100"
            >
              {languages.map((l) => (
                <option key={l.code} value={l.code}>
                  {l.label}
                </option>
              ))}
            </select>
            <LoginButton className="hidden sm:inline-flex" />
          </nav>
        </div>
      </header>

      <main>
        <section className="relative overflow-hidden">
          <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-[#d8f3ec] to-transparent" />
          <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 pb-16 pt-10 sm:px-6 md:grid-cols-[1.04fr_0.96fr] md:pb-20 md:pt-14 lg:px-8">
            <div className="relative z-10">
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-teal-200 bg-white px-3 py-2 text-xs font-bold uppercase tracking-[0.14em] text-teal-800 shadow-sm">
                <span className="h-2 w-2 rounded-full bg-[#00897B]" />
                {t('landing_eyebrow')}
              </div>
              <h1 className="max-w-3xl text-5xl font-black leading-[0.98] tracking-tight text-slate-950 sm:text-6xl lg:text-7xl">
                {t('landing_hero_title')}
              </h1>
              <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-600 sm:text-xl">
                {t('landing_hero_body')}
              </p>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
                <LoginButton className="w-full sm:w-auto" />
                <a
                  href="#workflow"
                  className="inline-flex min-h-12 items-center justify-center rounded-full border border-slate-200 bg-white px-6 py-3 text-sm font-semibold text-slate-800 shadow-sm transition hover:-translate-y-0.5 hover:border-slate-300"
                >
                  {t('landing_secondary_cta')}
                </a>
              </div>

              <div className="mt-9 grid grid-cols-3 gap-3 rounded-[1.35rem] border border-slate-200 bg-white p-2 shadow-sm">
                {stats.map((item) => (
                  <div key={item.label} className="rounded-2xl px-2 py-3 text-center sm:px-4">
                    <div className="text-xl font-black text-slate-950 sm:text-2xl">{t(item.value)}</div>
                    <div className="mt-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                      {t(item.label)}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative z-10">
              <ReaderMockup />
            </div>
          </div>
        </section>

        <section className="bg-white py-14 sm:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl">
              <p className="text-sm font-black uppercase tracking-[0.2em] text-[#1565C0]">
                {t('landing_features_eyebrow')}
              </p>
              <h2 className="mt-3 text-3xl font-black tracking-tight sm:text-5xl">
                {t('landing_features_title')}
              </h2>
            </div>

            <div className="mt-9 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {features.map((feature) => (
                <article
                  key={feature.title}
                  className="rounded-[1.25rem] border border-slate-200 bg-[#fbfcfc] p-5 transition hover:-translate-y-1 hover:shadow-xl hover:shadow-slate-900/8"
                >
                  <div className="mb-5 inline-flex h-12 min-w-12 items-center justify-center rounded-2xl bg-slate-950 px-3 text-xs font-black tracking-[0.12em] text-white">
                    {feature.icon}
                  </div>
                  <h3 className="text-lg font-black">{t(feature.title)}</h3>
                  <p className="mt-3 text-sm leading-7 text-slate-600">{t(feature.body)}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="workflow" className="py-14 sm:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid gap-10 lg:grid-cols-[0.82fr_1.18fr] lg:items-start">
              <div>
                <p className="text-sm font-black uppercase tracking-[0.2em] text-teal-700">
                  {t('landing_workflow_eyebrow')}
                </p>
                <h2 className="mt-3 text-3xl font-black tracking-tight sm:text-5xl">
                  {t('landing_workflow_title')}
                </h2>
                <p className="mt-5 text-lg leading-8 text-slate-600">
                  {t('landing_workflow_body')}
                </p>
              </div>

              <div className="space-y-3">
                {steps.map((step) => (
                  <div
                    key={step.number}
                    className="grid gap-4 rounded-[1.25rem] border border-slate-200 bg-white p-5 shadow-sm sm:grid-cols-[72px_1fr]"
                  >
                    <div className="text-3xl font-black text-[#1565C0]">{step.number}</div>
                    <div>
                      <h3 className="text-xl font-black">{t(step.title)}</h3>
                      <p className="mt-2 leading-7 text-slate-600">{t(step.body)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="bg-slate-950 py-14 text-white sm:py-20">
          <div className="mx-auto grid max-w-7xl gap-8 px-4 sm:px-6 md:grid-cols-[1fr_1.2fr] md:items-center lg:px-8">
            <div>
              <p className="text-sm font-black uppercase tracking-[0.2em] text-teal-200">
                {t('landing_languages_eyebrow')}
              </p>
              <h2 className="mt-3 text-3xl font-black tracking-tight sm:text-5xl">
                {t('landing_languages_title')}
              </h2>
              <p className="mt-5 text-lg leading-8 text-slate-300">
                {t('landing_languages_body')}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
              {languages.map((language) => (
                <button
                  key={language.code}
                  type="button"
                  onClick={() => setLocale(language.code)}
                  className={`min-h-20 rounded-2xl border px-3 py-4 text-left transition ${
                    locale === language.code
                      ? 'border-teal-300 bg-teal-300 text-slate-950'
                      : 'border-white/10 bg-white/5 text-white hover:border-white/25 hover:bg-white/10'
                  }`}
                >
                  <div className="text-xs font-black uppercase tracking-[0.18em] opacity-70">
                    {language.code}
                  </div>
                  <div className="mt-2 text-sm font-black">{language.label}</div>
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-white py-14 sm:py-20">
          <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <p className="text-sm font-black uppercase tracking-[0.2em] text-[#1565C0]">
                {t('landing_faq_eyebrow')}
              </p>
              <h2 className="mt-3 text-3xl font-black tracking-tight sm:text-5xl">
                {t('landing_faq_title')}
              </h2>
            </div>

            <div className="mt-9 divide-y divide-slate-200 rounded-[1.25rem] border border-slate-200 bg-[#fbfcfc]">
              {faq.map((item) => (
                <details key={item.question} className="group p-5 open:bg-white">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-left text-lg font-black">
                    {t(item.question)}
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-950 text-sm text-white transition group-open:rotate-45">
                      +
                    </span>
                  </summary>
                  <p className="mt-4 max-w-3xl leading-7 text-slate-600">{t(item.answer)}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 py-14 sm:px-6 sm:py-20 lg:px-8">
          <div className="mx-auto max-w-5xl rounded-[2rem] bg-[#dff4ef] px-5 py-10 text-center shadow-[0_28px_90px_rgba(15,118,110,0.18)] sm:px-10 sm:py-14">
            <h2 className="text-3xl font-black tracking-tight sm:text-5xl">{t('landing_cta_title')}</h2>
            <p className="mx-auto mt-5 max-w-2xl text-lg leading-8 text-slate-700">
              {t('landing_cta_body')}
            </p>
            <div className="mt-8">
              <LoginButton />
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 px-4 py-8 text-sm text-slate-500 sm:flex-row sm:items-center sm:justify-between sm:px-6 lg:px-8">
          <p>&copy; 2026 LangBook Reader</p>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="font-semibold hover:text-slate-950">
              {t('privacy')}
            </Link>
            <a
              href="https://github.com/dronov-dmitry/lang-book-reader"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold hover:text-slate-950"
            >
              GitHub
            </a>
            <a
              href="https://dronov-dmitry.github.io/lang-book-reader/#/login"
              className="font-semibold hover:text-slate-950"
            >
              {t('login')}
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
